import {
  OrganizationSubjectAdminResponse,
  OrgSubjectsService,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { COMPACT_INPUT } from "components/Inputs/compact";
import { Fragment, KeyboardEvent, useEffect, useState } from "react";
import CurrencyInput from "react-currency-input-field";
import Notifications from "util/notifications";
import { LABEL, SECTION, SECTION_TEXT, SECTION_TITLE } from "./settingsStyles";

/** The most a rate can be, in dollars, as before. */
const MAX_DOLLARS = 5000;

const dollars = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/** A row being added or edited: the name, and the rates in dollars as typed. */
type Draft = { name: string; price: string; pay: string };

const toDraft = (subject?: OrganizationSubjectAdminResponse): Draft => ({
  name: subject?.name ?? "",
  price: subject ? String((subject.default_price_for_students ?? 0) / 100) : "",
  pay: subject ? String((subject.default_pay_for_tutors ?? 0) / 100) : "",
});

/** Dollars as typed to cents; empty is $0. */
const toCents = (text: string) => Math.round(Number(text || 0) * 100);

const byName = (
  a: OrganizationSubjectAdminResponse,
  b: OrganizationSubjectAdminResponse
) => a.name.localeCompare(b.name);

const CELL = "py-2.5 pr-3 align-middle";

/**
 * Organization Settings' Subjects tab: each subject's default hourly rates
 * (what students pay, what tutors receive) and whether it is active, with
 * edit and add in the table. It loads and saves on its own.
 */
export default function SubjectSettings({ orgId }: { orgId: number }) {
  const [subjects, setSubjects] =
    useState<OrganizationSubjectAdminResponse[]>();
  // The subject being edited, or "new" while one is being added.
  const [editing, setEditing] = useState<number | "new">();
  const [draft, setDraft] = useState<Draft>(toDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    OrgSubjectsService.getSubjectsByOrg({ orgId })
      .then((list) => setSubjects(list as OrganizationSubjectAdminResponse[]))
      .catch(() => Notifications.error("Subjects could not be loaded."));
  }, [orgId]);

  const replace = (updated: OrganizationSubjectAdminResponse) =>
    setSubjects((list) =>
      list?.map((subject) => (subject.id === updated.id ? updated : subject))
    );

  const startEditing = (subject?: OrganizationSubjectAdminResponse) => {
    setEditing(subject ? subject.id : "new");
    setDraft(toDraft(subject));
  };

  const overLimit =
    Number(draft.price || 0) > MAX_DOLLARS ||
    Number(draft.pay || 0) > MAX_DOLLARS;
  const canSave = !saving && draft.name.trim() !== "" && !overLimit;

  const save = () => {
    if (!canSave || editing === undefined) return;
    setSaving(true);
    const rates = {
      name: draft.name.trim(),
      default_price_for_students: toCents(draft.price),
      default_pay_for_tutors: toCents(draft.pay),
    };
    (editing === "new"
      ? OrgSubjectsService.createOrgSubject({
          requestBody: { org_id: orgId, ...rates, active: true },
        }).then((created) => {
          setSubjects((list) => [...(list ?? []), created]);
          Notifications.success("Subject created!");
        })
      : OrgSubjectsService.updateSubject({
          subjectId: editing,
          requestBody: rates,
        }).then((updated) => {
          replace(updated);
          Notifications.success("Subject edited!");
        })
    )
      .then(() => setEditing(undefined))
      .catch(() =>
        Notifications.error(
          editing === "new"
            ? "Unable to create subject."
            : "Unable to edit subject."
        )
      )
      .finally(() => setSaving(false));
  };

  const setActive = (subject: OrganizationSubjectAdminResponse) => {
    const active = !subject.active;
    OrgSubjectsService.updateSubject({
      subjectId: subject.id,
      requestBody: { active },
    })
      .then((updated) => {
        replace(updated);
        Notifications.success(
          `Subject ${active ? "reactivated" : "deactivated"}!`
        );
      })
      .catch(() => Notifications.error("Unable to change subject's status."));
  };

  // Enter saves the row, Escape leaves it. Escape is marked handled, so the
  // Settings dialog doesn't close too (and drop its unsaved settings).
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") save();
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setEditing(undefined);
    }
  };

  const editRow = (status: string) => (
    <tr className="bg-gray-50">
      <td className={`${CELL} pl-2`}>
        <input
          type="text"
          aria-label="Subject name"
          className={`${COMPACT_INPUT} w-full`}
          placeholder="Physics"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          onKeyDown={onKeyDown}
          autoFocus
        />
      </td>
      <td className={CELL}>
        <CurrencyInput
          aria-label="Students pay per hour"
          className={`${COMPACT_INPUT} w-full`}
          prefix="$"
          placeholder="$0.00"
          decimalsLimit={2}
          allowNegativeValue={false}
          value={draft.price}
          onValueChange={(value) => setDraft({ ...draft, price: value ?? "" })}
          onKeyDown={onKeyDown}
        />
      </td>
      <td className={CELL}>
        <CurrencyInput
          aria-label="Tutors receive per hour"
          className={`${COMPACT_INPUT} w-full`}
          prefix="$"
          placeholder="$0.00"
          decimalsLimit={2}
          allowNegativeValue={false}
          value={draft.pay}
          onValueChange={(value) => setDraft({ ...draft, pay: value ?? "" })}
          onKeyDown={onKeyDown}
        />
      </td>
      <td className={`${CELL} text-gray-500`}>{status}</td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        <div className="inline-flex gap-2">
          <Button
            size={ButtonSize.EXTRA_SMALL}
            color={canSave ? ButtonColor.GREEN : ButtonColor.GRAY}
            disabled={!canSave}
            onClick={save}
          >
            {editing === "new" ? "Add" : "Save"}
          </Button>
          <Button
            size={ButtonSize.EXTRA_SMALL}
            color={ButtonColor.GRAY}
            onClick={() => setEditing(undefined)}
          >
            Cancel
          </Button>
        </div>
      </td>
    </tr>
  );

  return (
    <section className={`${SECTION} max-w-3xl`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className={SECTION_TITLE}>Subjects</h3>
          <p className={SECTION_TEXT}>
            Each subject&apos;s default hourly rates: what students pay and what
            tutors receive.
          </p>
        </div>
        {editing !== "new" && (
          <Button
            size={ButtonSize.SMALL}
            color={ButtonColor.GREEN}
            fill={ButtonFill.HOLLOW}
            extraClasses="shrink-0"
            onClick={() => startEditing()}
          >
            Add subject
          </Button>
        )}
      </div>

      {!subjects ? (
        <p className="mt-4 text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <table className="mt-4 w-full table-fixed text-sm">
            <thead>
              <tr className={`border-b border-gray-200 text-left ${LABEL}`}>
                <th className="w-[30%] pb-2 pr-3">Subject</th>
                <th className="w-[16%] pb-2 pr-3">Students pay</th>
                <th className="w-[16%] pb-2 pr-3">Tutors receive</th>
                <th className="w-[12%] pb-2 pr-3">Status</th>
                <th className="pb-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...subjects].sort(byName).map((subject) =>
                editing === subject.id ? (
                  <Fragment key={subject.id}>
                    {editRow(subject.active ? "Active" : "Inactive")}
                  </Fragment>
                ) : (
                  <tr key={subject.id}>
                    <td
                      className={`${CELL} truncate font-medium ${
                        subject.active ? "text-neutral-900" : "text-gray-400"
                      }`}
                      title={subject.name}
                    >
                      {subject.name}
                    </td>
                    <td className={CELL}>
                      {dollars.format(
                        (subject.default_price_for_students ?? 0) / 100
                      )}
                    </td>
                    <td className={CELL}>
                      {dollars.format(
                        (subject.default_pay_for_tutors ?? 0) / 100
                      )}
                    </td>
                    <td className={CELL}>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          subject.active
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {subject.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className={`${CELL} whitespace-nowrap text-right`}>
                      <div className="inline-flex gap-2">
                        <Button
                          size={ButtonSize.EXTRA_SMALL}
                          color={ButtonColor.BLACK}
                          fill={ButtonFill.HOLLOW}
                          aria-label={`Edit ${subject.name}`}
                          onClick={() => startEditing(subject)}
                        >
                          Edit
                        </Button>
                        <Button
                          size={ButtonSize.EXTRA_SMALL}
                          color={
                            subject.active
                              ? ButtonColor.ORANGE
                              : ButtonColor.GREEN
                          }
                          fill={ButtonFill.HOLLOW}
                          aria-label={`${
                            subject.active ? "Deactivate" : "Reactivate"
                          } ${subject.name}`}
                          onClick={() => setActive(subject)}
                        >
                          {subject.active ? "Deactivate" : "Reactivate"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              )}
              {editing === "new" && editRow("Active")}
            </tbody>
          </table>
          {subjects.length === 0 && editing !== "new" && (
            <p className="py-4 text-sm text-gray-500">No subjects yet.</p>
          )}
          {editing !== undefined && overLimit && (
            <p className="mt-2 text-xs text-red-600">
              Rates can be at most {dollars.format(MAX_DOLLARS)}.
            </p>
          )}
        </>
      )}
    </section>
  );
}
